import { z } from "zod";

/**
 * Square checkout integration (venue owner connects her Square account and
 * maps each Art Days date to a Square catalog variation). Contract lives in
 * the API; keep these schemas in step with it.
 */

/** GET /v2/square/status — also the response shape of POST /v2/square/disconnect. */
export const SquareStatusSchema = z.object({
  configured: z.boolean(),
  connected: z.boolean(),
  merchant_id: z.string().nullable(),
  business_name: z.string().nullable(),
  location_id: z.string().nullable(),
  connected_at: z.string().nullable(),
});
export type SquareStatus = z.infer<typeof SquareStatusSchema>;

/** GET /v2/square/oauth/connect — browser is sent to `authorize_url` next. */
export const SquareConnectResponseSchema = z.object({
  authorize_url: z.string(),
});
export type SquareConnectResponse = z.infer<typeof SquareConnectResponseSchema>;

/** One sellable Square item variation from the merchant's catalog. */
export const SquareCatalogItemSchema = z.object({
  variation_id: z.string(),
  item_name: z.string(),
  variation_name: z.string(),
  price_cents: z.number().int().nullable(),
  currency: z.string().nullable(),
});
export type SquareCatalogItem = z.infer<typeof SquareCatalogItemSchema>;

/** GET /v2/square/catalog */
export const SquareCatalogResponseSchema = z.object({
  items: z.array(SquareCatalogItemSchema),
});
export type SquareCatalogResponse = z.infer<typeof SquareCatalogResponseSchema>;

/**
 * One Art Days session. `square_variation_id` null means no Square checkout
 * for that date (the site shows the free sign-up instead).
 */
export const SquareArtDaySchema = z.object({
  id: z.string(),
  session_date: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  capacity: z.number().int(),
  taken: z.number().int(),
  square_variation_id: z.string().nullable(),
  square_price_cents: z.number().int().nullable(),
});
export type SquareArtDay = z.infer<typeof SquareArtDaySchema>;

/** GET /v2/square/art-days */
export const SquareArtDayListResponseSchema = z.object({
  items: z.array(SquareArtDaySchema),
});
export type SquareArtDayListResponse = z.infer<typeof SquareArtDayListResponseSchema>;

// ---- Mutation inputs -------------------------------------------------------

/** PUT /v2/square/art-days/{id} — body is `{variation_id}` only. */
export interface SquareMapArtDayInput {
  id: string;
  variation_id: string | null;
}
